import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { Layout } from '@/components/Layout';
import { functions } from '@/lib/firebase';
import { SERVICE_LINES } from '@/lib/affiliate';

/**
 * Public, prospect-facing referral page. An affiliate shares their personal link
 * (/r/THEIR_CODE) after a real conversation; the interested prospect fills in
 * their own details and consents to be contacted. Submitting creates a CRM lead
 * tagged to the affiliate with a consent record — the prospect never touches
 * Firestore directly (a Cloud Function does the write).
 */
export function ReferralLandingPage() {
  const { code = '' } = useParams<{ code: string }>();

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [serviceLine, setServiceLine] = useState('');
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!email.trim() && !phone.trim()) {
      setError('Please give us an email or a phone number so we can reach you.');
      return;
    }
    if (!consent) {
      setError('Please tick the box to let GRID contact you.');
      return;
    }

    setSubmitting(true);
    try {
      const submit = httpsCallable(functions, 'submitReferralLead');
      await submit({
        code: code.trim(),
        name: name.trim(),
        company: company.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        serviceLine: serviceLine || undefined,
        message: message.trim() || undefined,
        consent: true,
      });
      setDone(true);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Layout>
        <div style={{ maxWidth: '480px', margin: '0 auto', paddingTop: '24px' }}>
          <div className="terminal-card">
            <div className="card-header"><span className="card-title">Thank You</span></div>
            <div className="card-body">
              <div style={{ fontSize: '2.5rem', color: 'var(--gold)', fontWeight: 700, marginBottom: '0.5rem' }}>✓</div>
              <p className="prose">Thanks, {name.trim().split(' ')[0]}! Your details are in — someone from GRID will reach out soon.</p>
              <p className="prose prose--sm prose--muted">In the meantime, feel free to explore what GRID does.</p>
              <Link to="/" className="action-btn large" style={{ display: 'inline-block', textDecoration: 'none', marginTop: '8px' }}>
                Visit GRID
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ maxWidth: '520px', margin: '0 auto', paddingTop: '24px' }}>
        <div className="terminal-card">
          <div className="card-header">
            <span className="card-title">Get in Touch with GRID</span>
          </div>
          <div className="card-body">
            <p className="prose prose--sm prose--muted" style={{ marginTop: 0 }}>
              You were referred to <strong style={{ color: 'var(--text)' }}>GRID</strong> — we build websites,
              applications, AI automation, and blockchain tools for businesses. Leave your details below and
              we'll reach out about your project. No obligation.
            </p>

            {error && <div className="terminal-alert error">{error}</div>}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="field-group">
                <label className="field-label" htmlFor="ref-name">Your Name *</label>
                <input id="ref-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} className="terminal-input" placeholder="Jane Smith" />
              </div>

              <div className="field-group">
                <label className="field-label" htmlFor="ref-company">Business / Company</label>
                <input id="ref-company" type="text" value={company} onChange={(e) => setCompany(e.target.value)} className="terminal-input" placeholder="Optional" />
              </div>

              <div className="field-group">
                <label className="field-label" htmlFor="ref-email">Email</label>
                <input id="ref-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="terminal-input" placeholder="you@business.com" />
              </div>

              <div className="field-group">
                <label className="field-label" htmlFor="ref-phone">Phone</label>
                <input id="ref-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="terminal-input" placeholder="Optional" />
              </div>
              <span style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '-8px' }}>Give us an email or a phone number — whichever you prefer.</span>

              <div className="field-group">
                <label className="field-label" htmlFor="ref-service">What are you interested in?</label>
                <select id="ref-service" value={serviceLine} onChange={(e) => setServiceLine(e.target.value)} className="terminal-input">
                  <option value="">Not sure yet</option>
                  {SERVICE_LINES.filter((s) => s.key !== 'OTHER').map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                  <option value="OTHER">Something else</option>
                </select>
              </div>

              <div className="field-group">
                <label className="field-label" htmlFor="ref-message">Tell us a bit about your project</label>
                <textarea id="ref-message" value={message} onChange={(e) => setMessage(e.target.value)} className="terminal-textarea" placeholder="Optional — what are you looking to build or fix?" />
              </div>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: '3px', accentColor: 'var(--accent)' }} />
                <span className="prose prose--sm prose--muted" style={{ margin: 0 }}>
                  I agree that GRID may contact me about my project using the details I've provided.
                </span>
              </label>

              <button type="submit" className="action-btn large" disabled={submitting} style={{ width: '100%' }}>
                {submitting ? 'Sending…' : 'Send My Details'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
}
